import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  Badge,
  useTheme,
  Alert
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { WarningAmberRounded } from '@mui/icons-material';
import logo from '../assets/logo.webp';
import { useCart } from '../contexts/CartContext';
import UserMenu from './UserMenu';

const pages = [
  { title: 'Home', path: '/', description: 'Return to homepage' },
  { title: 'Shop', path: '/products', description: 'Browse our products' },
  { title: 'Clothing Guide', path: '/clothing-guide', description: 'Learn about protective clothing' },
  { title: 'About', path: '/about', description: 'Learn about Begard Gear' },
  { title: 'Contact', path: '/contact', description: 'Get in touch with us' },
];

const Navbar = () => {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const { cartItems } = useCart();
  const muiTheme = useTheme();

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };
  
  // Let the theme handle AppBar styling completely
  
  // Menu paper styles
  const menuPaperStyles = {
    backgroundColor: muiTheme.palette.background.paper,
    border: '1px solid',
    borderColor: 'secondary.light',
  };
  
  // Button styles
  const buttonStyles = {
    my: 2, 
    mx: 0.5,
    color: 'secondary.light', 
    display: 'block', 
    fontWeight: 600,
    letterSpacing: '0.05rem',
    '&:hover': {
      color: 'secondary.main',
      backgroundColor: 'rgba(217, 165, 102, 0.08)' // Reduced opacity for better accessibility
    }
  };
      
  // Colors
  const textColor = 'secondary.light';
  const iconColor = 'secondary.light';
  const iconHoverColor = 'secondary.main';
  
  // Environment detection
  const environment = process.env.ENVIRONMENT || process.env.REACT_APP_ENVIRONMENT || 'dev';
  const isDev = environment === 'dev' || environment === 'development';

  return (
    <Box component="header" role="banner">
      {/* Development Environment Alert */}
      {isDev && (
        <Alert 
          severity="warning"
          icon={<WarningAmberRounded sx={{ fontSize: { xs: 28, md: 32 }, color: '#8B4513', mr: 1.5 }} aria-hidden="true" />} 
          sx={{
            fontSize: { xs: '0.9rem', md: '1.1rem' },
            fontWeight: 'bold',
            justifyContent: 'center',
            textAlign: 'center',
            py: 1,
            borderRadius: 0,
            backgroundColor: '#FFF3CD', // Softer yellow background
            color: '#664D03', // Darker brown text for better contrast
            border: '1px solid #FFECB5', // Subtle border
          }}
          role="alert"
        >
          !! Development Environment !!
        </Alert>
      )}

      <AppBar 
        position="static" 
        component="nav"
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* Logo for larger screens */}
            <Box
              component={RouterLink}
              to="/"
              aria-label="Begard Gear Home"
              sx={{
                mr: 2,
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                textDecoration: 'none',
                color: textColor,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
                  animation: 'logoShimmer 4s ease-in-out 1',
                  pointerEvents: 'none',
                  zIndex: 1,
                },
                '@keyframes logoShimmer': {
                  '0%': {
                    left: '-100%',
                  },
                  '50%': {
                    left: '100%',
                  },
                  '100%': {
                    left: '-100%',
                  },
                },
              }}
            >
              <img 
                src={logo} 
                alt="Begard Gear Logo" 
                style={{ height: '50px', marginRight: '12px' }}
                loading="eager"
                width="50"
                height="50"
              />
              <Typography
                variant="h4"
                noWrap
                component="span"
                sx={{
                  fontFamily: '"Bebas Neue", "Roboto Condensed", "Arial Black", sans-serif',
                  fontWeight: 900,
                  letterSpacing: '.22rem',
                  textTransform: 'uppercase',
                  background: 'linear-gradient(45deg, #D9A566 30%, #EFB366 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    transition: 'transform 0.2s ease-in-out',
                  }
                }}
              >
                BEGARD GEAR
              </Typography>
            </Box>

            {/* Mobile menu */}
            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label={anchorElNav ? "Close menu" : "Open menu"}
                aria-controls={anchorElNav ? "menu-appbar" : undefined}
                aria-expanded={Boolean(anchorElNav)}
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                sx={{
                  color: iconColor,
                  '&:hover': { color: iconHoverColor }
                }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: 'block', md: 'none' },
                }}
                PaperProps={{
                  sx: menuPaperStyles
                }}
                role="menu"
              >
                {pages.map((page) => (
                  <MenuItem 
                    key={page.path}
                    onClick={handleCloseNavMenu}
                    component={RouterLink}
                    to={page.path}
                    role="menuitem"
                    aria-label={page.description}
                  >
                    <Typography textAlign="center" color={textColor}>
                      {page.title}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            {/* Logo for mobile */}
            <Box
              component={RouterLink}
              to="/"
              aria-label="Begard Gear Home"
              sx={{
                flexGrow: 1,
                display: { xs: 'flex', md: 'none' },
                alignItems: 'center',
                textDecoration: 'none',
                color: textColor,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
                  animation: 'logoShimmer 4s ease-in-out 1',
                  pointerEvents: 'none',
                  zIndex: 1,
                },
                '@keyframes logoShimmer': {
                  '0%': {
                    left: '-100%',
                  },
                  '50%': {
                    left: '100%',
                  },
                  '100%': {
                    left: '-100%',
                  },
                },
              }}
            >
              <img 
                src={logo} 
                alt="Begard Gear Logo" 
                style={{ height: '40px', marginRight: '8px' }}
                loading="eager"
                width="40"
                height="40"
              />
              <Typography
                variant="h6"
                noWrap
                component="span"
                sx={{
                  fontFamily: '"Bebas Neue", "Roboto Condensed", "Arial Black", sans-serif',
                  fontWeight: 900,
                  letterSpacing: '.18rem',
                  textTransform: 'uppercase',
                  fontSize: '1.35rem',
                  background: 'linear-gradient(45deg, #D9A566 30%, #EFB366 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textShadow: '1.5px 1.5px 3px rgba(0,0,0,0.3)',
                }}
              >
                BEGARD GEAR
              </Typography>
            </Box>

            {/* Desktop navigation */}
            <Box 
              sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}
              component="nav"
              aria-label="Main navigation"
            >
              {pages.map((page) => (
                <Button
                  key={page.path}
                  component={RouterLink}
                  to={page.path}
                  onClick={handleCloseNavMenu}
                  sx={buttonStyles}
                  aria-label={page.description}
                >
                  {page.title}
                </Button>
              ))}
            </Box>

            {/* Cart and User Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton 
                component={RouterLink}
                to="/cart"
                size="large"
                aria-label={`Shopping cart with ${cartItems.length} items`}
                sx={{
                  color: iconColor,
                  '&:hover': { color: iconHoverColor }
                }}
              >
                <Badge badgeContent={cartItems.length} color="error">
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>
              <UserMenu />
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    </Box>
  );
};

export default Navbar; 